import React, { useState } from 'react';
import { Box, Button, CircularProgress, Menu, MenuItem, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  INTEREST_STAGES,
  INTEREST_STAGE_COLORS,
  formatInterestOffer,
  getInterestStageLabel,
  getLeadLostReasonLabel,
} from '../leadsConfig';
import { colors, typography } from '../../styles/theme';
import { LEADS_TEXT_SECONDARY } from './leadsUi.styles';
import { stageDotSx } from './StageDot';

const dotSx = (stage) => stageDotSx(INTEREST_STAGE_COLORS[stage]);

/** Texto corto de la etapa: "Ofertado · USD 120.000". */
export function formatInterestStageText(item) {
  const label = getInterestStageLabel(item?.stage);
  const offer = (item?.stage === 'ofertado' || item?.stage === 'reservado')
    ? formatInterestOffer(item?.offer_amount, item?.offer_currency)
    : '';
  return offer ? `${label} · ${offer}` : label;
}

/** Tooltip: motivo de descarte (si hay). */
export function getInterestStageTooltip(item) {
  if (item?.stage === 'descartado' && item?.discard_reason) {
    return `Motivo: ${getLeadLostReasonLabel(item.discard_reason)}`;
  }
  return '';
}

/**
 * Etapa de un interés (V3-3): texto chico con punto de color. Editable → abre
 * un menú con los estilos de "Ordenar por:" y las 7 etapas.
 *
 * @param {{ item: object, editable?: boolean, busy?: boolean, muted?: boolean,
 *   onSelect?: (stage: string) => void }} props
 */
export default function LeadInterestStageMenu({ item, editable = false, busy = false, muted = false, onSelect }) {
  const [anchor, setAnchor] = useState(null);
  const stage = item?.stage;
  if (!stage) return null;
  const text = formatInterestStageText(item);
  const tooltip = getInterestStageTooltip(item);
  const content = (
    <>
      {busy ? <CircularProgress size={10} sx={{ color: 'inherit' }} /> : <Box component="span" sx={dotSx(stage)} />}
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{text}</Box>
    </>
  );
  const baseSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    color: muted ? colors.textSecondary : colors.textPanelTitle,
    fontFamily: typography.fontFamily.primary,
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 1.2,
    flexShrink: 0,
  };

  if (!editable) {
    const node = (
      <Box component="span" role="status" aria-label={`Etapa: ${text}`} data-testid="interest-stage" sx={{ ...baseSx, px: muted ? 0 : 0.5 }}>
        {content}
      </Box>
    );
    return tooltip ? <Tooltip title={tooltip} placement="top" arrow>{node}</Tooltip> : node;
  }

  const button = (
    <Button
      size="small"
      onClick={(e) => setAnchor(e.currentTarget)}
      disabled={busy}
      endIcon={<ExpandMoreIcon sx={{ fontSize: '16px !important' }} />}
      aria-label={`Etapa: ${text}. Cambiar etapa`}
      aria-haspopup="menu"
      aria-expanded={Boolean(anchor)}
      data-testid="interest-stage"
      sx={{
        ...baseSx,
        textTransform: 'none',
        minHeight: 24,
        minWidth: 0,
        px: 0.5,
        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: '#FFFFFF' },
        '&.Mui-disabled': { color: LEADS_TEXT_SECONDARY },
        '& .MuiButton-endIcon': { ml: 0.25, color: colors.textSecondary },
      }}
    >
      {content}
    </Button>
  );

  return (
    <>
      {tooltip ? <Tooltip title={tooltip} placement="top" arrow><span>{button}</span></Tooltip> : button}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            bgcolor: colors.backgroundDark,
            backgroundImage: 'none',
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            minWidth: 180,
          },
        }}
      >
        {INTEREST_STAGES.map((value) => (
          <MenuItem
            key={value}
            selected={stage === value}
            onClick={() => {
              setAnchor(null);
              onSelect?.(value);
            }}
            sx={{
              gap: 1,
              color: colors.textPrimary,
              fontSize: '13px',
              fontFamily: typography.fontFamily.primary,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.10)' },
            }}
          >
            <Box component="span" sx={dotSx(value)} />
            {getInterestStageLabel(value)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
